import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify-icon/react';
import Link from 'next/link';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import PageLink from '@/utils/PageLink';

interface TableOfContentsItem {
  id: string;
  title: string;
  level?: number;
}

interface NavLink {
  title: string;
  href: string;
}

interface DocsArticleLayoutProps {
  category: string;
  /**
   * Kept as a Tailwind text class (e.g. "text-green-700") for backwards
   * compatibility with the original articles, but it now only picks an accent
   * from the palette below rather than being interpolated into class names —
   * interpolated classes were being purged by Tailwind and silently rendering
   * unstyled.
   */
  categoryColor: string;
  title: string;
  description: string;
  readingTime: string;
  lastUpdated?: string;
  tableOfContents: TableOfContentsItem[];
  prevArticle?: NavLink;
  nextArticle?: NavLink;
  keyTakeaways?: string[];
  children: ReactNode;
}

type AccentName = 'green' | 'blue' | 'purple' | 'orange' | 'rose' | 'primary';

/**
 * Every accent class is written out in full so Tailwind's content scanner can
 * see it. The previous implementation built class names with `.replace()` at
 * runtime, which produced strings Tailwind never emitted CSS for.
 */
const ACCENTS: Record<
  AccentName,
  { text: string; chip: string; bar: string; hex: string; takeaway: string }
> = {
  green: {
    text: 'text-emerald-700',
    chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    bar: 'bg-emerald-500',
    hex: '#059669',
    takeaway: 'text-emerald-600',
  },
  blue: {
    text: 'text-sky-700',
    chip: 'bg-sky-50 text-sky-700 ring-sky-200',
    bar: 'bg-sky-500',
    hex: '#0284c7',
    takeaway: 'text-sky-600',
  },
  purple: {
    text: 'text-violet-700',
    chip: 'bg-violet-50 text-violet-700 ring-violet-200',
    bar: 'bg-violet-500',
    hex: '#7c3aed',
    takeaway: 'text-violet-600',
  },
  orange: {
    text: 'text-amber-700',
    chip: 'bg-amber-50 text-amber-700 ring-amber-200',
    bar: 'bg-amber-500',
    hex: '#d97706',
    takeaway: 'text-amber-600',
  },
  rose: {
    text: 'text-rose-700',
    chip: 'bg-rose-50 text-rose-700 ring-rose-200',
    bar: 'bg-rose-500',
    hex: '#e11d48',
    takeaway: 'text-rose-600',
  },
  primary: {
    text: 'text-primary-700',
    chip: 'bg-primary-50 text-primary-700 ring-primary-200',
    bar: 'bg-primary-600',
    hex: '#004790',
    takeaway: 'text-primary-600',
  },
};

const resolveAccent = (categoryColor: string): AccentName => {
  if (categoryColor.includes('green') || categoryColor.includes('emerald')) return 'green';
  if (categoryColor.includes('blue') || categoryColor.includes('sky')) return 'blue';
  if (categoryColor.includes('purple') || categoryColor.includes('violet')) return 'purple';
  if (categoryColor.includes('orange') || categoryColor.includes('amber')) return 'orange';
  if (categoryColor.includes('rose') || categoryColor.includes('red')) return 'rose';
  return 'primary';
};

const DocsArticleLayout: React.FC<DocsArticleLayoutProps> = ({
  category,
  categoryColor,
  title,
  description,
  readingTime,
  lastUpdated,
  tableOfContents,
  prevArticle,
  nextArticle,
  children,
  keyTakeaways,
}) => {
  const [activeSection, setActiveSection] = useState<string>('');
  const [readingProgress, setReadingProgress] = useState(0);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  const accent = useMemo(() => ACCENTS[resolveAccent(categoryColor)], [categoryColor]);

  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map((item) => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 150;

      let newActiveSection = '';
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          newActiveSection = tableOfContents[i].id;
          break;
        }
      }
      setActiveSection(newActiveSection);

      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const progress =
        documentHeight > 0 ? Math.min(Math.round((scrolled / documentHeight) * 100), 100) : 0;
      setReadingProgress(progress);
    };

    // Reveal each section heading's accent underline as it scrolls into view.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const h2 = entry.target.querySelector('h2');
            if (h2) h2.classList.add('section-active');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -100px 0px', threshold: 0 }
    );

    document.querySelectorAll('.docs-article-content section[id]').forEach((section) => {
      observer.observe(section);
    });

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [tableOfContents]);

  const scrollToSection = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (!element) return;
    const yOffset = -100;
    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
    setMobileTocOpen(false);
  }, []);

  const copyAnchorLink = useCallback(async (id: string) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }, []);

  // Attach a "copy link to this section" affordance to every h2/h3 that has an id.
  useEffect(() => {
    const contentEl = document.querySelector('.docs-article-content');
    if (!contentEl) return;

    contentEl.querySelectorAll('h2[id], h3[id]').forEach((heading) => {
      if (heading.querySelector('.anchor-link')) return;

      const id = heading.getAttribute('id');
      if (!id) return;

      const anchor = document.createElement('button');
      anchor.type = 'button';
      anchor.className = 'anchor-link';
      anchor.setAttribute('aria-label', 'Copy link to this section');
      anchor.innerHTML =
        '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>';
      anchor.title = 'Copy link to section';
      anchor.onclick = (e) => {
        e.preventDefault();
        void copyAnchorLink(id);
      };

      heading.insertBefore(anchor, heading.firstChild);
    });
  }, [children, copyAnchorLink]);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Reading progress */}
      <div className="fixed left-0 right-0 top-0 z-50 h-0.5 bg-transparent">
        <div
          className={`h-full transition-[width] duration-150 ease-out ${accent.bar}`}
          style={{ width: `${readingProgress}%` }}
          role="progressbar"
          aria-label="Article reading progress"
          aria-valuenow={readingProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>

      {/* Article header */}
      <header className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-4 pb-10 pt-10 sm:px-6 lg:px-8 lg:pb-14 lg:pt-14">
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-sm">
            <Link
              href={PageLink.docsHub}
              className="text-slate-500 transition hover:text-slate-900">
              Docs
            </Link>
            <Icon icon="tabler:chevron-right" className="text-xs text-slate-300" />
            <span className={`font-medium ${accent.text}`}>{category}</span>
          </nav>

          <div className="max-w-3xl">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ring-1 ${accent.chip}`}>
              {category}
            </span>
            <h1 className="mt-4 font-display text-[2.125rem] font-semibold leading-[1.15] tracking-tight text-slate-900 sm:text-[2.75rem]">
              {title}
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">{description}</p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
              <span className="flex items-center gap-1.5">
                <Icon icon="tabler:clock" className="text-base text-slate-400" />
                {readingTime}
              </span>
              {lastUpdated && (
                <span className="flex items-center gap-1.5">
                  <Icon icon="tabler:refresh" className="text-base text-slate-400" />
                  Updated {lastUpdated}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile table of contents */}
      {tableOfContents.length > 0 && (
        <div className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur lg:hidden">
          <button
            type="button"
            onClick={() => setMobileTocOpen((open) => !open)}
            aria-expanded={mobileTocOpen}
            className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-slate-700">
            <span className="flex items-center gap-2">
              <Icon icon="tabler:list" className="text-base text-slate-400" />
              On this page
            </span>
            <Icon
              icon={mobileTocOpen ? 'tabler:chevron-up' : 'tabler:chevron-down'}
              className="text-base text-slate-400"
            />
          </button>
          {mobileTocOpen && (
            <nav className="max-h-[60vh] overflow-y-auto border-t border-slate-100 px-2 pb-3 pt-2">
              {tableOfContents.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full rounded-md px-3 py-2 text-left text-sm ${
                    item.level === 2 ? 'pl-6' : ''
                  } ${
                    activeSection === item.id
                      ? 'bg-slate-100 font-medium text-slate-900'
                      : 'text-slate-600'
                  }`}>
                  {item.title}
                </button>
              ))}
            </nav>
          )}
        </div>
      )}

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex gap-10 py-10 lg:gap-16 lg:py-16">
          {/* Desktop sidebar */}
          <aside className="hidden w-60 flex-shrink-0 lg:block">
            <div className="sticky top-24">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                On this page
              </p>
              <nav className="border-l border-slate-200">
                {tableOfContents.map((item) => {
                  const isActive = activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => scrollToSection(item.id)}
                      className={`-ml-px block w-full border-l-2 py-1.5 pr-2 text-left text-[13px] leading-snug transition-colors ${
                        item.level === 2 ? 'pl-7' : 'pl-4'
                      } ${
                        isActive
                          ? 'border-primary-600 font-medium text-primary-700'
                          : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-900'
                      }`}>
                      {item.title}
                    </button>
                  );
                })}
              </nav>

              <div className="mt-8 border-t border-slate-200 pt-6">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Quick links
                </p>
                <div className="space-y-2.5">
                  <Link
                    href={PageLink.docsHub}
                    className="flex items-center gap-2 text-[13px] text-slate-600 transition hover:text-primary-700">
                    <Icon icon="tabler:books" className="text-base text-slate-400" />
                    All documentation
                  </Link>
                  <Link
                    href={PageLink.docsApiReference}
                    className="flex items-center gap-2 text-[13px] text-slate-600 transition hover:text-primary-700">
                    <Icon icon="tabler:code" className="text-base text-slate-400" />
                    API reference
                  </Link>
                  <Link
                    href={PageLink.contactUs}
                    className="flex items-center gap-2 text-[13px] text-slate-600 transition hover:text-primary-700">
                    <Icon icon="tabler:lifebuoy" className="text-base text-slate-400" />
                    Get support
                  </Link>
                </div>
              </div>
            </div>
          </aside>

          <main className="min-w-0 max-w-[46rem] flex-1">
            {keyTakeaways && keyTakeaways.length > 0 && (
              <div className="mb-12 rounded-2xl border border-slate-200 bg-slate-50 p-6">
                <div className="mb-4 flex items-center gap-2">
                  <Icon icon="tabler:sparkles" className={`text-lg ${accent.takeaway}`} />
                  <h2 className="!m-0 text-sm font-semibold uppercase tracking-wider text-slate-700">
                    Key takeaways
                  </h2>
                </div>
                <ul className="!m-0 !list-none space-y-2.5 !p-0">
                  {keyTakeaways.map((takeaway) => (
                    <li key={takeaway} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <Icon
                        icon="tabler:check"
                        className={`mt-0.5 flex-shrink-0 text-base ${accent.takeaway}`}
                      />
                      <span className="leading-relaxed">{takeaway}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div
              className="docs-article-content prose prose-slate prose-headings:scroll-mt-28 prose-p:leading-[1.75] prose-p:text-slate-700 prose-a:font-medium prose-a:text-primary-700 prose-a:no-underline hover:prose-a:underline prose-strong:text-slate-900 prose-code:rounded prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:text-[0.85em] prose-code:font-medium prose-code:text-slate-800 prose-code:before:content-none prose-code:after:content-none prose-li:leading-[1.75] prose-li:text-slate-700 max-w-none"
              style={{ '--accent-color': accent.hex } as React.CSSProperties}>
              {children}
            </div>

            {/* Support CTA */}
            <div className="mt-16 rounded-2xl border border-slate-200 bg-slate-50 px-6 py-5">
              <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    Still stuck on something here?
                  </p>
                  <p className="mt-0.5 text-sm text-slate-600">
                    Send us the article and the step you&apos;re on — we answer with specifics.
                  </p>
                </div>
                <Link
                  href={PageLink.contactUs}
                  className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-primary-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-700">
                  Contact support
                  <Icon icon="tabler:arrow-right" className="text-base" />
                </Link>
              </div>
            </div>

            {/* Prev / next */}
            {(prevArticle || nextArticle) && (
              <nav
                aria-label="Article navigation"
                className="mt-8 grid gap-3 border-t border-slate-200 pt-8 sm:grid-cols-2">
                {prevArticle ? (
                  <Link
                    href={prevArticle.href}
                    className="group rounded-xl border border-slate-200 px-4 py-3 transition hover:border-primary-300 hover:bg-slate-50">
                    <span className="flex items-center gap-1.5 text-xs text-slate-400">
                      <Icon
                        icon="tabler:arrow-left"
                        className="text-sm transition-transform group-hover:-translate-x-0.5"
                      />
                      Previous
                    </span>
                    <span className="mt-1 block text-sm font-medium text-slate-900">
                      {prevArticle.title}
                    </span>
                  </Link>
                ) : (
                  <div className="hidden sm:block" />
                )}
                {nextArticle && (
                  <Link
                    href={nextArticle.href}
                    className="group rounded-xl border border-slate-200 px-4 py-3 text-right transition hover:border-primary-300 hover:bg-slate-50 sm:col-start-2">
                    <span className="flex items-center justify-end gap-1.5 text-xs text-slate-400">
                      Next
                      <Icon
                        icon="tabler:arrow-right"
                        className="text-sm transition-transform group-hover:translate-x-0.5"
                      />
                    </span>
                    <span className="mt-1 block text-sm font-medium text-slate-900">
                      {nextArticle.title}
                    </span>
                  </Link>
                )}
              </nav>
            )}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DocsArticleLayout;
